import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Search, Filter, RefreshCw, CheckCircle2, XCircle, Clock, Terminal, Database, Activity, MoreVertical, X, ChevronDown, ShieldAlert, Zap, User, Cpu, Copy, CheckSquare, Square, MinusSquare, Eye, Ban, FileText, ArrowUp, ArrowDown } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Tabs } from '../ui/Tabs';
import { Pagination } from '../ui/Pagination';
interface AIErrorControlCenterProps {
  success: (msg: string) => void;
  setDetailPanel: (panel: any) => void;
}
// --- MOCK DATA ---
const ERROR_TYPES = ['API Timeout', 'Rate limit exceeded', 'Token overflow', 'Invalid response', 'Parsing error', 'Prompt error', 'Model error', 'System error'];
const MODELS = ['GPT-5', 'Claude 3.5', 'Llama 3'];
const REPORT_TYPES = ['Натальная карта', 'Совместимость', 'Прогноз', 'Соляр', 'Комбо'];
const SOURCES: Array<'user' | 'astrologer' | 'system'> = ['user', 'astrologer', 'system'];
const USER_NAMES = ['Анна Петрова', 'Иван Сидоров', 'Maria Schmidt', 'Олег Козлов', 'Елена Волкова', 'Дмитрий Новиков', 'Светлана Иванова', 'Алексей Смирнов', 'Ольга Кузнецова', 'Наталья Морозова', 'Сергей Попов', 'Екатерина Лебедева'];
function randomFrom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function generateMockErrors(count: number) {
  return Array.from({
    length: count
  }, (_, i) => {
    const errorType = randomFrom(ERROR_TYPES);
    let severity: 'critical' | 'medium' | 'low' = 'medium';
    if (['API Timeout', 'System error', 'Model error'].includes(errorType)) {
      severity = Math.random() > 0.5 ? 'critical' : 'medium';
    } else if (['Parsing error', 'Prompt error'].includes(errorType)) {
      severity = 'low';
    }
    const statusRand = Math.random();
    const status = statusRand > 0.7 ? 'new' : statusRand > 0.4 ? 'in_progress' : statusRand > 0.15 ? 'resolved' : 'ignored';
    const model = randomFrom(MODELS);
    const reportType = randomFrom(REPORT_TYPES);
    const source = randomFrom(SOURCES);
    const createdDate = new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000);
    const retries = status === 'resolved' ? Math.floor(Math.random() * 3) + 1 : Math.floor(Math.random() * 3);
    return {
      id: `ERR-${3000 + i}`,
      genId: `GEN-${8000 + Math.floor(Math.random() * 1000)}`,
      errorType,
      severity,
      status,
      model,
      userId: `USR-${1000 + Math.floor(Math.random() * 500)}`,
      userName: randomFrom(USER_NAMES),
      reportType,
      source,
      retries,
      maxRetries: 3,
      tokens: Math.floor(Math.random() * 7000) + 1000,
      duration: `${Math.floor(Math.random() * 120)} сек`,
      errorText: `${errorType}: Failed to process request after ${retries} attempts. Connection reset by peer.`,
      stackTrace: `Error: ${errorType}\n    at GenerationService.process (server/src/services/generation.ts:142:23)\n    at async Worker.handleJob (server/src/workers/ai.ts:56:12)\n    at async Queue.process (node_modules/bullmq/lib/queue.js:412:9)\n    at async Runner.run (node_modules/bullmq/lib/runner.js:89:7)`,
      apiResponse: `{"error":{"code":"${errorType.toLowerCase().replace(/\s/g, '_')}","message":"${errorType}","status":${errorType === 'Rate limit exceeded' ? 429 : errorType === 'API Timeout' ? 504 : 500}}}`,
      promptTokens: Math.floor(Math.random() * 4000) + 500,
      completionTokens: Math.floor(Math.random() * 2000),
      templateId: `TPL-0${Math.floor(Math.random() * 5) + 1}`,
      orderId: `ORD-${5000 + Math.floor(Math.random() * 500)}`,
      createdAt: createdDate.toISOString(),
      updatedAt: new Date(createdDate.getTime() + Math.random() * 3600000).toISOString(),
      resolvedAt: status === 'resolved' ? new Date(createdDate.getTime() + Math.random() * 7200000).toISOString() : null,
      resolvedBy: status === 'resolved' ? randomFrom(['Система (auto-retry)', 'Админ Алексей', 'Админ Мария']) : null,
      retryHistory: Array.from({
        length: retries
      }, (__, r) => ({
        attempt: r + 1,
        model: r === 0 ? model : randomFrom(MODELS),
        status: r < retries - 1 ? 'failed' : status === 'resolved' ? 'success' : 'failed',
        error: r < retries - 1 ? errorType : status === 'resolved' ? null : errorType,
        time: new Date(createdDate.getTime() + (r + 1) * 60000).toISOString()
      })),
      userInput: {
        birthDate: randomFrom(['1990-03-15', '1985-07-22', '1998-11-08', '2001-01-30', '1976-06-12']),
        birthTime: randomFrom(['14:30', '08:15', '23:45', '06:00', '17:20']),
        birthPlace: randomFrom(['Москва, Россия', 'Санкт-Петербург, Россия', 'Берлин, Германия', 'Париж, Франция'])
      }
    };
  }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}
const INITIAL_DATA = generateMockErrors(200);
const SEVERITY_CONFIG: Record<string, {
  label: string;
  variant: any;
  icon: any;
  color: string;
}> = {
  critical: {
    label: 'Critical',
    variant: 'error',
    icon: ShieldAlert,
    color: 'text-rose-600'
  },
  medium: {
    label: 'Medium',
    variant: 'warning',
    icon: AlertTriangle,
    color: 'text-amber-600'
  },
  low: {
    label: 'Low',
    variant: 'neutral',
    icon: Activity,
    color: 'text-slate-500'
  }
};
const STATUS_CONFIG: Record<string, {
  label: string;
  variant: any;
}> = {
  new: {
    label: 'Новая',
    variant: 'error'
  },
  in_progress: {
    label: 'В работе',
    variant: 'warning'
  },
  resolved: {
    label: 'Решена',
    variant: 'success'
  },
  ignored: {
    label: 'Игнор',
    variant: 'neutral'
  }
};
const ERROR_TYPE_CONFIG: Record<string, {
  color: string;
}> = {
  'API Timeout': {
    color: 'bg-rose-100 text-rose-700'
  },
  'Rate limit exceeded': {
    color: 'bg-orange-100 text-orange-700'
  },
  'Token overflow': {
    color: 'bg-amber-100 text-amber-700'
  },
  'Invalid response': {
    color: 'bg-purple-100 text-purple-700'
  },
  'Parsing error': {
    color: 'bg-blue-100 text-blue-700'
  },
  'Prompt error': {
    color: 'bg-indigo-100 text-indigo-700'
  },
  'Model error': {
    color: 'bg-red-100 text-red-700'
  },
  'System error': {
    color: 'bg-slate-200 text-slate-700'
  }
};
export function AIErrorControlCenter({
  success,
  setDetailPanel
}: AIErrorControlCenterProps) {
  const [data, setData] = useState(INITIAL_DATA);
  const [isPaused, setIsPaused] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    severity: 'all',
    model: 'all',
    type: 'all',
    reportType: 'all',
    period: 'all'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [detailTab, setDetailTab] = useState('main');
  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [showAlert, setShowAlert] = useState(true);
  const [showBatchMenu, setShowBatchMenu] = useState(false);
  // Live updates
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setData(prev => {
        const newData = [...prev];
        // Randomly resolve some in_progress
        const ipIdx = newData.findIndex(d => d.status === 'in_progress');
        if (ipIdx !== -1 && Math.random() > 0.7) {
          newData[ipIdx] = {
            ...newData[ipIdx],
            status: 'resolved',
            updatedAt: new Date().toISOString(),
            resolvedAt: new Date().toISOString(),
            resolvedBy: 'Система (auto-retry)'
          };
        }
        // Occasionally add new error
        if (Math.random() > 0.8) {
          const newErr = generateMockErrors(1)[0];
          newErr.createdAt = new Date().toISOString();
          newErr.status = 'new';
          newErr.id = `ERR-${3200 + Math.floor(Math.random() * 800)}`;
          return [newErr, ...newData];
        }
        return newData;
      });
    }, 10000);
    return () => clearInterval(interval);
  }, [isPaused]);
  // Click outside
  useEffect(() => {
    const handleClick = () => {
      setOpenMenuId(null);
      setShowBatchMenu(false);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);
  // Analytics
  const stats = useMemo(() => {
    const unresolved = data.filter(d => ['new', 'in_progress'].includes(d.status));
    const critical = unresolved.filter(d => d.severity === 'critical');
    const resolvedToday = data.filter(d => d.status === 'resolved' && d.resolvedAt && new Date(d.resolvedAt).getTime() > Date.now() - 86400000);
    const byModel: Record<string, number> = {};
    const byType: Record<string, number> = {};
    const bySeverity: Record<string, number> = {
      critical: 0,
      medium: 0,
      low: 0
    };
    data.forEach(d => {
      byModel[d.model] = (byModel[d.model] || 0) + 1;
      byType[d.errorType] = (byType[d.errorType] || 0) + 1;
      bySeverity[d.severity] = (bySeverity[d.severity] || 0) + 1;
    });
    return {
      total: data.length,
      unresolved: unresolved.length,
      critical: critical.length,
      resolvedToday: resolvedToday.length,
      byModel,
      byType,
      bySeverity
    };
  }, [data]);
  // Filtering
  const filteredData = useMemo(() => {
    let result = data.filter(item => {
      const q = searchQuery.toLowerCase();
      const matchesSearch = !q || item.id.toLowerCase().includes(q) || item.genId.toLowerCase().includes(q) || item.userName.toLowerCase().includes(q);
      const matchesStatus = filters.status === 'all' || item.status === filters.status;
      const matchesSeverity = filters.severity === 'all' || item.severity === filters.severity;
      const matchesModel = filters.model === 'all' || item.model === filters.model;
      const matchesType = filters.type === 'all' || item.errorType === filters.type;
      const matchesReportType = filters.reportType === 'all' || item.reportType === filters.reportType;
      let matchesPeriod = true;
      if (filters.period !== 'all') {
        const now = Date.now();
        const created = new Date(item.createdAt).getTime();
        if (filters.period === 'today') matchesPeriod = created > now - 86400000;else if (filters.period === '7d') matchesPeriod = created > now - 7 * 86400000;else if (filters.period === '30d') matchesPeriod = created > now - 30 * 86400000;
      }
      return matchesSearch && matchesStatus && matchesSeverity && matchesModel && matchesType && matchesReportType && matchesPeriod;
    });
    // Sort
    result.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'createdAt') cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();else if (sortField === 'severity') {
        const sMap: Record<string, number> = {
          critical: 3,
          medium: 2,
          low: 1
        };
        cmp = (sMap[a.severity] || 0) - (sMap[b.severity] || 0);
      } else if (sortField === 'retries') cmp = a.retries - b.retries;else if (sortField === 'id') cmp = a.id.localeCompare(b.id);
      return sortDir === 'desc' ? -cmp : cmp;
    });
    return result;
  }, [data, searchQuery, filters, sortField, sortDir]);
  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  // Selection
  const toggleSelectAll = () => {
    if (selectedIds.size === paginatedData.length) setSelectedIds(new Set());else setSelectedIds(new Set(paginatedData.map(d => d.id)));
  };
  const toggleSelect = (id: string) => {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id);else s.add(id);
    setSelectedIds(s);
  };
  // Actions
  const handleAction = (action: string, item: any, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setOpenMenuId(null);
    switch (action) {
      case 'view':
        setSelectedItem(item);
        setDetailTab('main');
        setShowDetailModal(true);
        break;
      case 'retry':
        setData(prev => prev.map(d => d.id === item.id ? {
          ...d,
          status: 'in_progress' as const,
          retries: d.retries + 1
        } : d));
        success(`Генерация ${item.genId} отправлена на повтор`);
        break;
      case 'resolve':
        setData(prev => prev.map(d => d.id === item.id ? {
          ...d,
          status: 'resolved' as const,
          updatedAt: new Date().toISOString(),
          resolvedAt: new Date().toISOString(),
          resolvedBy: 'Админ'
        } : d));
        success(`Ошибка ${item.id} решена`);
        break;
      case 'ignore':
        setData(prev => prev.map(d => d.id === item.id ? {
          ...d,
          status: 'ignored' as const
        } : d));
        success(`Ошибка ${item.id} проигнорирована`);
        break;
      case 'change_model':
        const newModel = item.model === 'GPT-5' ? 'Claude 3.5' : item.model === 'Claude 3.5' ? 'Llama 3' : 'GPT-5';
        setData(prev => prev.map(d => d.id === item.id ? {
          ...d,
          model: newModel,
          status: 'in_progress' as const,
          retries: d.retries + 1
        } : d));
        success(`Модель → ${newModel}, повтор запущен`);
        break;
      case 'open_gen':
        setDetailPanel({
          type: 'ai_generation',
          title: `Генерация ${item.genId}`,
          data: {
            id: item.genId
          }
        });
        break;
      case 'open_user':
        setDetailPanel({
          type: 'user',
          title: item.userName,
          data: {
            id: item.userId
          }
        });
        break;
    }
  };
  const handleBatchAction = (action: string) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    switch (action) {
      case 'retry':
        setData(prev => prev.map(d => ids.includes(d.id) ? {
          ...d,
          status: 'in_progress' as const,
          retries: d.retries + 1
        } : d));
        success(`${ids.length} ошибок отправлены на повтор`);
        break;
      case 'resolve':
        setData(prev => prev.map(d => ids.includes(d.id) ? {
          ...d,
          status: 'resolved' as const,
          resolvedAt: new Date().toISOString(),
          resolvedBy: 'Админ (batch)'
        } : d));
        success(`${ids.length} ошибок решены`);
        break;
      case 'ignore':
        setData(prev => prev.map(d => ids.includes(d.id) ? {
          ...d,
          status: 'ignored' as const
        } : d));
        success(`${ids.length} ошибок проигнорированы`);
        break;
    }
    setSelectedIds(new Set());
    setShowBatchMenu(false);
  };
  const handleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');else {
      setSortField(field);
      setSortDir('desc');
    }
  };
  const SortHeader = ({
    field,
    children
  }: {
    field: string;
    children: React.ReactNode;
  }) => <th className="px-3 py-3 font-medium cursor-pointer hover:text-indigo-600 select-none" onClick={() => handleSort(field)} data-id="element-8529">
      <span className="flex items-center gap-1" data-id="element-8530">
        {children}
        {sortField === field && <ChevronDown className={`w-3 h-3 transition-transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} data-id="element-8531" />}
      </span>
    </th>;
  const timeAgo = (iso: string) => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins} мин назад`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} ч назад`;
    return `${Math.floor(hrs / 24)} дн назад`;
  };
  // --- DETAIL MODAL ---
  const renderDetailModal = () => {
    if (!selectedItem) return null;
    const SevIcon = SEVERITY_CONFIG[selectedItem.severity]?.icon || AlertTriangle;
    return <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={() => setShowDetailModal(false)} data-id="element-8532">
        <Card className="w-full max-w-5xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden" onClick={(e: React.MouseEvent) => e.stopPropagation()} data-id="element-8533">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-white shrink-0" data-id="element-8534">
            <div data-id="element-8535">
              <div className="flex items-center gap-3 mb-1" data-id="element-8536">
                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2" data-id="element-8537">
                  <SevIcon className={`w-5 h-5 ${SEVERITY_CONFIG[selectedItem.severity]?.color}`} data-id="element-8538" />
                  {selectedItem.id}
                </h2>
                <Badge variant={STATUS_CONFIG[selectedItem.status]?.variant} data-id="element-8539">
                  {STATUS_CONFIG[selectedItem.status]?.label}
                </Badge>
                <Badge variant={SEVERITY_CONFIG[selectedItem.severity]?.variant} data-id="element-8540">
                  {SEVERITY_CONFIG[selectedItem.severity]?.label}
                </Badge>
              </div>
              <p className="text-sm text-slate-500" data-id="element-8541">
                Генерация:{' '}
                <span className="font-medium text-indigo-600 cursor-pointer hover:underline" onClick={() => {
                setShowDetailModal(false);
                handleAction('open_gen', selectedItem);
              }} data-id="element-8542">
                  {selectedItem.genId}
                </span>
                {' • '}Создана:{' '}
                {new Date(selectedItem.createdAt).toLocaleString('ru-RU')}
              </p>
            </div>
            <div className="flex items-center gap-2" data-id="element-8543">
              {selectedItem.status !== 'resolved' && <>
                  <Button variant="primary" size="sm" icon={<RefreshCw className="w-4 h-4" data-id="element-8545" />} onClick={() => handleAction('retry', selectedItem)} data-id="element-8544">
                    Retry
                  </Button>
                  <Button variant="outline" size="sm" icon={<CheckCircle2 className="w-4 h-4" data-id="element-8547" />} onClick={() => handleAction('resolve', selectedItem)} data-id="element-8546">
                    Решить
                  </Button>
                </>}
              <button onClick={() => setShowDetailModal(false)} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 ml-2" data-id="element-8548">
                <X className="w-5 h-5" data-id="element-8549" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="px-6 border-b border-slate-100 bg-white shrink-0" data-id="element-8550">
            <Tabs activeTab={detailTab} onChange={setDetailTab} tabs={[{
            id: 'main',
            label: 'Основное'
          }, {
            id: 'cause',
            label: 'Причина'
          }, {
            id: 'prompt',
            label: 'Prompt'
          }, {
            id: 'model',
            label: 'Модель'
          }, {
            id: 'history',
            label: 'Попытки'
          }]} data-id="element-8551" />
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto bg-slate-50 flex-1" data-id="element-8552">
            {detailTab === 'main' && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-8553">
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8554">
                  <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-8555">
                    Детали ошибки
                  </h3>
                  <div className="space-y-3" data-id="element-8556">
                    {[['Тип ошибки', selectedItem.errorType], ['Severity', SEVERITY_CONFIG[selectedItem.severity]?.label], ['Статус', STATUS_CONFIG[selectedItem.status]?.label], ['Модель', selectedItem.model], ['Время до ошибки', selectedItem.duration], ['Попытки', `${selectedItem.retries}/${selectedItem.maxRetries}`]].map(([label, value]) => <div key={label as string} className="flex justify-between py-2 border-b border-slate-100" data-id="element-8557">
                        <span className="text-sm text-slate-500" data-id="element-8558">{label}</span>
                        <span className="text-sm font-medium text-slate-900" data-id="element-8559">
                          {value}
                        </span>
                      </div>)}
                  </div>
                </Card>
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8560">
                  <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-8561">
                    Связи
                  </h3>
                  <div className="space-y-3" data-id="element-8562">
                    <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-8563">
                      <span className="text-sm text-slate-500" data-id="element-8564">
                        Пользователь
                      </span>
                      <span className="text-sm font-medium text-indigo-600 cursor-pointer hover:underline" onClick={() => {
                    setShowDetailModal(false);
                    handleAction('open_user', selectedItem);
                  }} data-id="element-8565">
                        {selectedItem.userName} ({selectedItem.userId})
                      </span>
                    </div>
                    {[['Генерация', selectedItem.genId], ['Заказ', selectedItem.orderId], ['Шаблон', selectedItem.templateId], ['Источник', selectedItem.source], ['Тип отчёта', selectedItem.reportType]].map(([label, value]) => <div key={label as string} className="flex justify-between py-2 border-b border-slate-100" data-id="element-8566">
                        <span className="text-sm text-slate-500" data-id="element-8567">{label}</span>
                        <span className="text-sm font-medium text-slate-900" data-id="element-8568">
                          {value}
                        </span>
                      </div>)}
                    {selectedItem.resolvedBy && <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-8569">
                        <span className="text-sm text-slate-500" data-id="element-8570">Решена</span>
                        <span className="text-sm font-medium text-emerald-600" data-id="element-8571">
                          {selectedItem.resolvedBy}
                        </span>
                      </div>}
                  </div>
                </Card>
              </div>}

            {detailTab === 'cause' && <div className="space-y-6" data-id="element-8572">
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8573">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-8574">
                    Сообщение об ошибке
                  </h4>
                  <div className="p-4 bg-rose-50 border border-rose-200 rounded-lg" data-id="element-8575">
                    <div className="flex items-start gap-3" data-id="element-8576">
                      <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" data-id="element-8577" />
                      <div data-id="element-8578">
                        <p className="text-sm font-medium text-rose-900" data-id="element-8579">
                          {selectedItem.errorType}
                        </p>
                        <p className="text-sm text-rose-700 mt-1" data-id="element-8580">
                          {selectedItem.errorText}
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8581">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-8582">
                    Stack Trace
                  </h4>
                  <div className="p-4 bg-slate-900 text-rose-400 rounded-lg font-mono text-xs whitespace-pre-wrap max-h-[250px] overflow-y-auto" data-id="element-8583">
                    {selectedItem.stackTrace}
                  </div>
                </Card>
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8584">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-8585">
                    API Response
                  </h4>
                  <div className="p-4 bg-slate-900 text-amber-400 rounded-lg font-mono text-xs whitespace-pre-wrap max-h-[200px] overflow-y-auto" data-id="element-8586">
                    {JSON.stringify(JSON.parse(selectedItem.apiResponse), null, 2)}
                  </div>
                </Card>
              </div>}

            {detailTab === 'prompt' && <div className="space-y-6" data-id="element-8587">
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8588">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4" data-id="element-8589">
                    Данные пользователя
                  </h4>
                  <div className="space-y-3" data-id="element-8590">
                    {[['Дата рождения', selectedItem.userInput?.birthDate], ['Время рождения', selectedItem.userInput?.birthTime], ['Место рождения', selectedItem.userInput?.birthPlace]].map(([label, value]) => <div key={label as string} className="flex justify-between py-2 border-b border-slate-100" data-id="element-8591">
                        <span className="text-sm text-slate-500" data-id="element-8592">{label}</span>
                        <span className="text-sm font-medium text-slate-900" data-id="element-8593">
                          {value || '—'}
                        </span>
                      </div>)}
                  </div>
                </Card>
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8594">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-8595">
                    System Prompt
                  </h4>
                  <div className="p-4 bg-slate-900 text-emerald-400 rounded-lg font-mono text-xs whitespace-pre-wrap max-h-[200px] overflow-y-auto" data-id="element-8596">
                    {selectedItem.promptInfo?.system || 'N/A'}
                  </div>
                </Card>
                <div className="grid grid-cols-2 gap-4" data-id="element-8597">
                  <div className="p-4 bg-white border border-slate-200 rounded-lg" data-id="element-8598">
                    <div className="text-xs text-slate-500 mb-1" data-id="element-8599">
                      Prompt Tokens
                    </div>
                    <div className="text-lg font-bold text-slate-900" data-id="element-8600">
                      {selectedItem.promptTokens?.toLocaleString()}
                    </div>
                  </div>
                  <div className="p-4 bg-white border border-slate-200 rounded-lg" data-id="element-8601">
                    <div className="text-xs text-slate-500 mb-1" data-id="element-8602">
                      Completion Tokens
                    </div>
                    <div className="text-lg font-bold text-slate-900" data-id="element-8603">
                      {selectedItem.completionTokens?.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>}

            {detailTab === 'model' && <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8604">
                <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-8605">
                  Параметры модели
                </h3>
                <div className="space-y-3" data-id="element-8606">
                  {[['Модель', selectedItem.model], ['Temperature', '0.7'], ['Max Tokens', '8192'], ['Top P', '0.95'], ['Frequency Penalty', '0.0'], ['Шаблон', selectedItem.templateId]].map(([label, value]) => <div key={label as string} className="flex justify-between py-2 border-b border-slate-100" data-id="element-8607">
                      <span className="text-sm text-slate-500" data-id="element-8608">{label}</span>
                      <span className="text-sm font-medium text-slate-900" data-id="element-8609">
                        {value}
                      </span>
                    </div>)}
                </div>
                <div className="mt-6 flex gap-2" data-id="element-8610">
                  <Button variant="outline" size="sm" onClick={() => handleAction('change_model', selectedItem)} data-id="element-8611">
                    Сменить модель и повторить
                  </Button>
                </div>
              </Card>}

            {detailTab === 'history' && <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8612">
                <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-8613">
                  История попыток
                </h3>
                {selectedItem.retryHistory && selectedItem.retryHistory.length > 0 ? <div className="space-y-4" data-id="element-8614">
                    {selectedItem.retryHistory.map((r: any, idx: number) => <div key={idx} className="flex items-start gap-3" data-id="element-8615">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${r.status === 'success' ? 'bg-emerald-100' : 'bg-rose-100'}`} data-id="element-8616">
                          {r.status === 'success' ? <CheckCircle2 className="w-4 h-4 text-emerald-600" data-id="element-8617" /> : <XCircle className="w-4 h-4 text-rose-600" data-id="element-8618" />}
                        </div>
                        <div className="flex-1" data-id="element-8619">
                          <div className="flex items-center gap-2" data-id="element-8620">
                            <span className="text-sm font-medium text-slate-900" data-id="element-8621">
                              Попытка #{r.attempt}
                            </span>
                            <Badge variant={r.status === 'success' ? 'success' : 'error'} size="sm" data-id="element-8622">
                              {r.status === 'success' ? 'Успех' : 'Ошибка'}
                            </Badge>
                            <Badge variant="outline" size="sm" data-id="element-8623">
                              {r.model}
                            </Badge>
                          </div>
                          {r.error && <p className="text-xs text-rose-600 mt-1" data-id="element-8624">
                              {r.error}
                            </p>}
                          <p className="text-xs text-slate-400 mt-1" data-id="element-8625">
                            {new Date(r.time).toLocaleString('ru-RU')}
                          </p>
                        </div>
                      </div>)}
                  </div> : <div className="text-center py-8 text-slate-500" data-id="element-8626">
                    <Clock className="w-10 h-10 mx-auto mb-3 text-slate-300" data-id="element-8627" />
                    <p className="text-sm" data-id="element-8628">Попыток пока не было</p>
                  </div>}
              </Card>}
          </div>
        </Card>
      </div>;
  };
  // --- MAIN RENDER ---
  const criticalNew = data.filter(d => d.severity === 'critical' && d.status === 'new').length;
  return <div className="space-y-6" data-id="element-8629">
      {/* Alert Banner */}
      {showAlert && criticalNew > 3 && <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-start gap-3" data-id="element-8630">
          <ShieldAlert className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" data-id="element-8631" />
          <div className="flex-1" data-id="element-8632">
            <h4 className="text-sm font-bold text-rose-900" data-id="element-8633">
              ⚠ Обнаружен всплеск критических ошибок
            </h4>
            <p className="text-xs text-rose-700 mt-1" data-id="element-8634">
              {criticalNew} критических ошибок ожидают решения. Рекомендуем
              проверить статус моделей и API.
            </p>
          </div>
          <button onClick={() => setShowAlert(false)} className="p-1 text-rose-400 hover:text-rose-600" data-id="element-8635">
            <X className="w-4 h-4" data-id="element-8636" />
          </button>
        </div>}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-8637">
        <Card padding="sm" className="shadow-sm border-slate-200 cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => setFilters(f => ({
        ...f,
        status: 'all'
      }))} data-id="element-8638">
          <div className="flex items-center gap-3" data-id="element-8639">
            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center" data-id="element-8640">
              <Database className="w-5 h-5 text-slate-600" data-id="element-8641" />
            </div>
            <div data-id="element-8642">
              <div className="text-2xl font-bold text-slate-900" data-id="element-8643">
                {stats.total}
              </div>
              <div className="text-xs text-slate-500" data-id="element-8644">Всего ошибок</div>
            </div>
          </div>
        </Card>
        <Card padding="sm" className="shadow-sm border-slate-200 cursor-pointer hover:border-rose-300 transition-colors" onClick={() => setFilters(f => ({
        ...f,
        severity: 'critical',
        status: 'all'
      }))} data-id="element-8645">
          <div className="flex items-center gap-3" data-id="element-8646">
            <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center" data-id="element-8647">
              <ShieldAlert className="w-5 h-5 text-rose-600" data-id="element-8648" />
            </div>
            <div data-id="element-8649">
              <div className="text-2xl font-bold text-rose-600" data-id="element-8650">
                {stats.critical}
              </div>
              <div className="text-xs text-slate-500" data-id="element-8651">Критических</div>
            </div>
          </div>
        </Card>
        <Card padding="sm" className="shadow-sm border-slate-200 cursor-pointer hover:border-amber-300 transition-colors" onClick={() => setFilters(f => ({
        ...f,
        status: 'new'
      }))} data-id="element-8652">
          <div className="flex items-center gap-3" data-id="element-8653">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center" data-id="element-8654">
              <AlertTriangle className="w-5 h-5 text-amber-600" data-id="element-8655" />
            </div>
            <div data-id="element-8656">
              <div className="text-2xl font-bold text-amber-600" data-id="element-8657">
                {stats.unresolved}
              </div>
              <div className="text-xs text-slate-500" data-id="element-8658">Нерешённых</div>
            </div>
          </div>
        </Card>
        <Card padding="sm" className="shadow-sm border-slate-200 cursor-pointer hover:border-emerald-300 transition-colors" onClick={() => setFilters(f => ({
        ...f,
        status: 'resolved'
      }))} data-id="element-8659">
          <div className="flex items-center gap-3" data-id="element-8660">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center" data-id="element-8661">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" data-id="element-8662" />
            </div>
            <div data-id="element-8663">
              <div className="text-2xl font-bold text-emerald-600" data-id="element-8664">
                {stats.resolvedToday}
              </div>
              <div className="text-xs text-slate-500" data-id="element-8665">Решено сегодня</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Analytics Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-8666">
        <Card padding="sm" className="shadow-sm border-slate-200" data-id="element-8667">
          <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-8668">
            По моделям
          </h4>
          <div className="space-y-2" data-id="element-8669">
            {Object.entries(stats.byModel).sort((a, b) => b[1] - a[1]).map(([model, count]) => <button key={model} className="w-full flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-slate-50 transition-colors" onClick={() => setFilters(f => ({
            ...f,
            model
          }))} data-id="element-8670">
                  <span className="text-sm text-slate-700" data-id="element-8671">{model}</span>
                  <div className="flex items-center gap-2" data-id="element-8672">
                    <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-8673">
                      <div className="h-full bg-indigo-500 rounded-full" style={{
                  width: `${count / stats.total * 100}%`
                }} data-id="element-8674" />
                    </div>
                    <span className="text-xs font-bold text-slate-600 w-8 text-right" data-id="element-8675">
                      {count}
                    </span>
                  </div>
                </button>)}
          </div>
        </Card>
        <Card padding="sm" className="shadow-sm border-slate-200" data-id="element-8676">
          <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-8677">
            По типам ошибок
          </h4>
          <div className="space-y-1.5 max-h-[140px] overflow-y-auto" data-id="element-8678">
            {Object.entries(stats.byType).sort((a, b) => b[1] - a[1]).map(([type, count]) => <button key={type} className="w-full flex items-center justify-between py-1 px-2 rounded-lg hover:bg-slate-50 transition-colors" onClick={() => setFilters(f => ({
            ...f,
            type
          }))} data-id="element-8679">
                  <span className="text-xs text-slate-700 truncate" data-id="element-8680">
                    {type}
                  </span>
                  <span className="text-xs font-bold text-slate-600 shrink-0 ml-2" data-id="element-8681">
                    {count}
                  </span>
                </button>)}
          </div>
        </Card>
        <Card padding="sm" className="shadow-sm border-slate-200" data-id="element-8682">
          <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-8683">
            По severity
          </h4>
          <div className="space-y-3" data-id="element-8684">
            {Object.entries(stats.bySeverity).map(([sev, count]) => {
            const cfg = SEVERITY_CONFIG[sev];
            return <button key={sev} className="w-full flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-slate-50 transition-colors" onClick={() => setFilters(f => ({
              ...f,
              severity: sev
            }))} data-id="element-8685">
                  <div className="flex items-center gap-2" data-id="element-8686">
                    <div className={`w-3 h-3 rounded-full ${sev === 'critical' ? 'bg-rose-500' : sev === 'medium' ? 'bg-amber-500' : 'bg-slate-400'}`} data-id="element-8687" />
                    <span className="text-sm text-slate-700" data-id="element-8688">{cfg?.label}</span>
                  </div>
                  <span className="text-sm font-bold text-slate-900" data-id="element-8689">
                    {count}
                  </span>
                </button>;
          })}
          </div>
        </Card>
      </div>

      {/* Toolbar */}
      <Card padding="none" className="shadow-sm border-slate-200" data-id="element-8690">
        <div className="p-4 flex flex-wrap items-center gap-3 border-b border-slate-100" data-id="element-8691">
          <div className="flex-1 min-w-[200px] max-w-sm relative" data-id="element-8692">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-8693" />
            <input type="text" placeholder="Поиск по ID, генерации, пользователю..." value={searchQuery} onChange={e => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }} className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" data-id="element-8694" />
          </div>
          <Button variant="outline" size="sm" icon={<Filter className="w-4 h-4" data-id="element-8696" />} onClick={() => setShowFilters(!showFilters)} data-id="element-8695">
            Фильтры{' '}
            {Object.values(filters).filter(v => v !== 'all').length > 0 && <span className="ml-1 w-5 h-5 bg-indigo-600 text-white rounded-full text-[10px] flex items-center justify-center" data-id="element-8697">
                {Object.values(filters).filter(v => v !== 'all').length}
              </span>}
          </Button>
          {selectedIds.size > 0 && <div className="relative" data-id="element-8698">
              <Button variant="primary" size="sm" onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            setShowBatchMenu(!showBatchMenu);
          }} data-id="element-8699">
                Действия ({selectedIds.size}){' '}
                <ChevronDown className="w-3 h-3 ml-1" data-id="element-8700" />
              </Button>
              {showBatchMenu && <div className="absolute top-full left-0 mt-1 w-52 bg-white rounded-xl shadow-xl border border-slate-100 py-1 z-50" data-id="element-8701">
                  <button className="w-full text-left px-4 py-2 text-xs hover:bg-slate-50 flex items-center gap-2" onClick={() => handleBatchAction('retry')} data-id="element-8702">
                    <RefreshCw className="w-3.5 h-3.5" data-id="element-8703" /> Повторить все
                  </button>
                  <button className="w-full text-left px-4 py-2 text-xs hover:bg-slate-50 flex items-center gap-2" onClick={() => handleBatchAction('resolve')} data-id="element-8704">
                    <CheckCircle2 className="w-3.5 h-3.5" data-id="element-8705" /> Отметить решёнными
                  </button>
                  <button className="w-full text-left px-4 py-2 text-xs hover:bg-slate-50 flex items-center gap-2" onClick={() => handleBatchAction('ignore')} data-id="element-8706">
                    <Ban className="w-3.5 h-3.5" data-id="element-8707" /> Игнорировать
                  </button>
                </div>}
            </div>}
          <div className="ml-auto flex items-center gap-2" data-id="element-8708">
            <button onClick={() => setIsPaused(!isPaused)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${isPaused ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`} data-id="element-8709">
              <span className={`w-2 h-2 rounded-full ${isPaused ? 'bg-amber-500' : 'bg-emerald-500 animate-pulse'}`} data-id="element-8710" />
              {isPaused ? 'Пауза' : 'Live'}
            </button>
            <span className="text-xs text-slate-400" data-id="element-8711">
              {filteredData.length} записей
            </span>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && <div className="p-4 bg-slate-50 border-b border-slate-100 flex flex-wrap gap-3 items-end" data-id="element-8712">
            {[{
          key: 'status',
          label: 'Статус',
          options: [{
            value: 'all',
            label: 'Все'
          }, ...Object.entries(STATUS_CONFIG).map(([k, v]) => ({
            value: k,
            label: v.label
          }))]
        }, {
          key: 'severity',
          label: 'Severity',
          options: [{
            value: 'all',
            label: 'Все'
          }, ...Object.entries(SEVERITY_CONFIG).map(([k, v]) => ({
            value: k,
            label: v.label
          }))]
        }, {
          key: 'model',
          label: 'Модель',
          options: [{
            value: 'all',
            label: 'Все'
          }, ...MODELS.map(m => ({
            value: m,
            label: m
          }))]
        }, {
          key: 'type',
          label: 'Тип ошибки',
          options: [{
            value: 'all',
            label: 'Все'
          }, ...ERROR_TYPES.map(t => ({
            value: t,
            label: t
          }))]
        }, {
          key: 'reportType',
          label: 'Тип отчёта',
          options: [{
            value: 'all',
            label: 'Все'
          }, ...REPORT_TYPES.map(t => ({
            value: t,
            label: t
          }))]
        }, {
          key: 'period',
          label: 'Период',
          options: [{
            value: 'all',
            label: 'Все'
          }, {
            value: 'today',
            label: 'Сегодня'
          }, {
            value: '7d',
            label: '7 дней'
          }, {
            value: '30d',
            label: '30 дней'
          }]
        }].map(f => <div key={f.key} data-id="element-8713">
                <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1 block" data-id="element-8714">
                  {f.label}
                </label>
                <select value={(filters as any)[f.key]} onChange={e => {
            setFilters(prev => ({
              ...prev,
              [f.key]: e.target.value
            }));
            setCurrentPage(1);
          }} className="text-xs border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-8715">
                  {f.options.map(o => <option key={o.value} value={o.value} data-id="element-8716">
                      {o.label}
                    </option>)}
                </select>
              </div>)}
            <Button variant="ghost" size="sm" onClick={() => {
          setFilters({
            status: 'all',
            severity: 'all',
            model: 'all',
            type: 'all',
            reportType: 'all',
            period: 'all'
          });
          setCurrentPage(1);
        }} data-id="element-8717">
              Сбросить
            </Button>
          </div>}

        {/* Table */}
        <div className="overflow-x-auto" data-id="element-8718">
          <table className="w-full text-left" style={{
          minWidth: '1100px'
        }} data-id="element-8719">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[10px] uppercase tracking-wider" data-id="element-8720">
              <tr data-id="element-8721">
                <th className="px-3 py-3 w-10" data-id="element-8722">
                  <button onClick={toggleSelectAll} className="text-slate-400 hover:text-slate-600" data-id="element-8723">
                    {selectedIds.size === paginatedData.length && paginatedData.length > 0 ? <CheckSquare className="w-4 h-4" data-id="element-8724" /> : selectedIds.size > 0 ? <MinusSquare className="w-4 h-4" data-id="element-8725" /> : <Square className="w-4 h-4" data-id="element-8726" />}
                  </button>
                </th>
                <SortHeader field="id" data-id="element-8727">ID</SortHeader>
                <th className="px-3 py-3 font-medium" data-id="element-8728">Тип ошибки</th>
                <SortHeader field="severity" data-id="element-8729">Severity</SortHeader>
                <th className="px-3 py-3 font-medium" data-id="element-8730">Статус</th>
                <th className="px-3 py-3 font-medium" data-id="element-8731">Модель</th>
                <th className="px-3 py-3 font-medium" data-id="element-8732">Пользователь</th>
                <th className="px-3 py-3 font-medium" data-id="element-8733">Тип отчёта</th>
                <SortHeader field="createdAt" data-id="element-8734">Время</SortHeader>
                <SortHeader field="retries" data-id="element-8735">Retry</SortHeader>
                <th className="px-3 py-3 w-10" data-id="element-8736"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-8737">
              {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50 transition-colors group" data-id="element-8738">
                  <td className="px-3 py-3" data-id="element-8739">
                    <button onClick={() => toggleSelect(item.id)} className="text-slate-400 hover:text-slate-600" data-id="element-8740">
                      {selectedIds.has(item.id) ? <CheckSquare className="w-4 h-4 text-indigo-600" data-id="element-8741" /> : <Square className="w-4 h-4" data-id="element-8742" />}
                    </button>
                  </td>
                  <td className="px-3 py-3" data-id="element-8743">
                    <button className="text-xs font-bold text-rose-600 hover:underline" onClick={() => handleAction('view', item)} data-id="element-8744">
                      {item.id}
                    </button>
                    <button className="block text-[10px] text-indigo-600 hover:underline mt-0.5" onClick={() => handleAction('open_gen', item)} data-id="element-8745">
                      {item.genId}
                    </button>
                  </td>
                  <td className="px-3 py-3" data-id="element-8746">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${ERROR_TYPE_CONFIG[item.errorType]?.color || 'bg-slate-100 text-slate-700'}`} data-id="element-8747">
                      {item.errorType}
                    </span>
                  </td>
                  <td className="px-3 py-3" data-id="element-8748">
                    <Badge variant={SEVERITY_CONFIG[item.severity]?.variant} size="sm" data-id="element-8749">
                      {SEVERITY_CONFIG[item.severity]?.label}
                    </Badge>
                  </td>
                  <td className="px-3 py-3" data-id="element-8750">
                    <button onClick={e => {
                  e.stopPropagation();
                  const nextStatus: Record<string, string> = {
                    new: 'in_progress',
                    in_progress: 'resolved',
                    resolved: 'ignored',
                    ignored: 'new'
                  };
                  setData(prev => prev.map(d => d.id === item.id ? {
                    ...d,
                    status: nextStatus[d.status] || 'new',
                    updatedAt: new Date().toISOString()
                  } : d));
                }} data-id="element-8751">
                      <Badge variant={STATUS_CONFIG[item.status]?.variant} size="sm" className="cursor-pointer hover:opacity-80" data-id="element-8752">
                        {STATUS_CONFIG[item.status]?.label}
                      </Badge>
                    </button>
                  </td>
                  <td className="px-3 py-3" data-id="element-8753">
                    <Badge variant="outline" size="sm" className="text-[10px]" data-id="element-8754">
                      {item.model}
                    </Badge>
                  </td>
                  <td className="px-3 py-3" data-id="element-8755">
                    <button className="text-sm text-slate-900 hover:text-indigo-600 hover:underline" onClick={() => handleAction('open_user', item)} data-id="element-8756">
                      {item.userName}
                    </button>
                  </td>
                  <td className="px-3 py-3 text-xs text-slate-700" data-id="element-8757">
                    {item.reportType}
                  </td>
                  <td className="px-3 py-3" data-id="element-8758">
                    <span className="text-xs text-slate-500" title={new Date(item.createdAt).toLocaleString('ru-RU')} data-id="element-8759">
                      {timeAgo(item.createdAt)}
                    </span>
                  </td>
                  <td className="px-3 py-3" data-id="element-8760">
                    <span className={`text-xs font-medium ${item.retries >= item.maxRetries ? 'text-rose-600' : 'text-slate-600'}`} data-id="element-8761">
                      {item.retries}/{item.maxRetries}
                    </span>
                  </td>
                  <td className="px-3 py-3 relative" data-id="element-8762">
                    <button className="p-1 text-slate-400 hover:text-slate-600 rounded" onClick={e => {
                  e.stopPropagation();
                  setOpenMenuId(openMenuId === item.id ? null : item.id);
                }} data-id="element-8763">
                      <MoreVertical className="w-4 h-4" data-id="element-8764" />
                    </button>
                    {openMenuId === item.id && <div className="absolute right-0 top-9 w-52 bg-white rounded-xl shadow-xl border border-slate-100 py-1 z-[9999]" data-id="element-8765">
                        <button className="w-full text-left px-4 py-2 text-xs hover:bg-slate-50 flex items-center gap-2" onClick={e => handleAction('view', item, e)} data-id="element-8766">
                          <Eye className="w-3.5 h-3.5" data-id="element-8767" /> Открыть ошибку
                        </button>
                        <button className="w-full text-left px-4 py-2 text-xs hover:bg-slate-50 flex items-center gap-2" onClick={e => handleAction('open_gen', item, e)} data-id="element-8768">
                          <FileText className="w-3.5 h-3.5" data-id="element-8769" /> Открыть генерацию
                        </button>
                        <div className="h-px bg-slate-100 my-1" data-id="element-8770" />
                        <button className="w-full text-left px-4 py-2 text-xs hover:bg-slate-50 flex items-center gap-2" onClick={e => handleAction('retry', item, e)} data-id="element-8771">
                          <RefreshCw className="w-3.5 h-3.5" data-id="element-8772" /> Повторить
                        </button>
                        <button className="w-full text-left px-4 py-2 text-xs hover:bg-slate-50 flex items-center gap-2" onClick={e => handleAction('change_model', item, e)} data-id="element-8773">
                          <Cpu className="w-3.5 h-3.5" data-id="element-8774" /> Сменить модель
                        </button>
                        <div className="h-px bg-slate-100 my-1" data-id="element-8775" />
                        <button className="w-full text-left px-4 py-2 text-xs text-emerald-600 hover:bg-emerald-50 flex items-center gap-2" onClick={e => handleAction('resolve', item, e)} data-id="element-8776">
                          <CheckCircle2 className="w-3.5 h-3.5" data-id="element-8777" /> Решена
                        </button>
                        <button className="w-full text-left px-4 py-2 text-xs text-slate-500 hover:bg-slate-50 flex items-center gap-2" onClick={e => handleAction('ignore', item, e)} data-id="element-8778">
                          <Ban className="w-3.5 h-3.5" data-id="element-8779" /> Игнорировать
                        </button>
                      </div>}
                  </td>
                </tr>)}
              {paginatedData.length === 0 && <tr data-id="element-8780">
                  <td colSpan={11} className="text-center py-12 text-slate-500" data-id="element-8781">
                    <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-slate-300" data-id="element-8782" />
                    <p className="text-sm font-medium" data-id="element-8783">Ошибок не найдено</p>
                    <p className="text-xs mt-1" data-id="element-8784">Попробуйте изменить фильтры</p>
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && <div className="p-4 border-t border-slate-100 flex items-center justify-between" data-id="element-8785">
            <span className="text-xs text-slate-500" data-id="element-8786">
              Показано {(currentPage - 1) * itemsPerPage + 1}–
              {Math.min(currentPage * itemsPerPage, filteredData.length)} из{' '}
              {filteredData.length}
            </span>
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-8787" />
          </div>}
      </Card>

      {/* Detail Modal */}
      {showDetailModal && renderDetailModal()}
    </div>;
}